import {
  DatabaseFieldType,
  type DatabaseAiSummaryEmptyOutcome
} from '@databases/types/database';
import { createElement, type ReactNode } from 'react';
import { resolveRichTextValue } from '@canvas/adf/resolve-rich-text-value';
import { defineFieldReadModule } from '../field-read-module.ts';
import { type RichTextReadContent } from '../rich-text/rich-text-field-read-module.ts';
import { AiSummaryValueView } from './ai-summary-value-view.tsx';

export type AiSummaryEmptyOutcomeLabels = Readonly<
  Record<DatabaseAiSummaryEmptyOutcome['kind'], string>
>;
export type AiSummaryReadContent =
  | RichTextReadContent
  | {
      readonly kind: 'ai-summary-empty';
      readonly text: string;
    };
export type AiSummaryReadRenderer = (content: AiSummaryReadContent) => ReactNode;
const renderValue: AiSummaryReadRenderer = (content) =>
  createElement(AiSummaryValueView, { content });

/**
 * V1 converts only legacy Text. Composition supplies localized empty outcomes
 * only when its AI eligibility policy permits them; generation remains external.
 */
export function createAiSummaryFieldReadModule({
  render = renderValue,
  emptyOutcomeLabels
}: {
  readonly render?: AiSummaryReadRenderer;
  readonly emptyOutcomeLabels?: AiSummaryEmptyOutcomeLabels;
} = {}) {
  const labels = emptyOutcomeLabels && Object.freeze({ ...emptyOutcomeLabels });

  return defineFieldReadModule<AiSummaryReadContent>({
    fieldType: DatabaseFieldType.AI_SUMMARY,
    render,
    compile: (field) =>
      Object.freeze({
        fieldId: field.id,
        resolve(storedValue, defaultValue) {
          const value = storedValue ?? defaultValue;
          const text =
            value?.type === DatabaseFieldType.AI_SUMMARY ||
            value?.type === DatabaseFieldType.TEXT
              ? value.value
              : { plain: '' };

          /**
           * Keep this V1 rule local to avoid importing the AI generation runtime.
           */
          if (
            labels &&
            value?.type === DatabaseFieldType.AI_SUMMARY &&
            text.plain.trim() === ''
          ) {
            const metadata = value.value.metadata;
            const outcome = metadata?.emptyOutcome;

            if (
              outcome &&
              metadata.lastUpdatedAt === undefined &&
              metadata.lastUpdatedBy === undefined &&
              metadata.generation === undefined
            ) {
              return Object.freeze({
                kind: 'ai-summary-empty' as const,
                text: labels[outcome.kind]
              });
            }
          }

          return Object.freeze({
            kind: 'rich-text' as const,
            document: resolveRichTextValue(text)
          });
        }
      })
  });
}
